import dayjs from 'dayjs'
import type { FilterModel, AdvancedFilterModel } from 'ag-grid-community'
import type { SortModelItem } from 'ag-grid-enterprise'
import type {
  IdeaAlert,
  IdeaListItem,
  IdeaListRequest,
  IdeaOrder,
} from '@/apis/idea/idea-api.def.ts'
import type { SSRMGridFetchParams } from '@/common/ag-grid/grid.types.ts'
import { sortMappingIdeaList } from './idea-list-table.def.ts'
import { FIRM_ROLE, GROUP_ROLE } from '@/router/roles.ts'
import { splitCategory } from '@/utils/selector-options.ts'

// ─── Unified Row Interface ───

/**
 * A flat row structure shared by both Idea (parent) and Action Log (child) rows.
 * Enables ag-grid SSRM Row Grouping with a single set of columnDefs.
 */
export interface UnifiedIdeaRow {
  // Row identity & grouping
  id: string // idea id (used as group key)
  rowType: 'idea' | 'order' // discriminator for rendering logic
  actionId?: string // only for actionLog rows
  orderId?: string // only for order rows

  // Shared display fields
  tickerCode: string
  tickerName: string
  tickerLegalName: string | null
  direction: 'Long' | 'Short'
  currentMarketValue: number | null
  fillingProgress: number | null
  dailyReturnPct: number | null
  quarterlyReturnPct: number | null
  totalReturnPct: number | null
  targetMarketValue: number | null
  avgCost: number | null
  currentQty: number | null
  targetQty: number | null
  tickerCurrency: string | null
  targetPrice: number | null
  stopPrice: number | null
  horizon: string | null
  conviction: string | null
  tradeReason: string | null
  fillStrategy: string | null
  auto_fill: boolean | null
  createdAt: Date | string | null
  state: string | null
  overallCost: number | null
  alerts: IdeaAlert[] | null
  liquidateReason: string | null
  closedAt: string | null
  duration: number | null
  contributor: string | null
  firmName: string | null
  marketTimeZone: string | null

  // Idea-only fields (context menu, row class rules, etc.)
  canEdit?: boolean
  lastFillingState?: string
  lastFilledResult?: string
  isTrial?: boolean
  showAlert?: boolean

  // Idea-only PnL fields preserved for existing columns
  dailyPnl?: number | null
  quarterPnl?: number | null
  currentTotalPnl?: number | null
  cumulativePnl?: number | null
  pctGMV?: number | null
  pctBP?: number | null
  shares?: number | null

  // Action log specific
  actionType?: string
  generatedBySystem?: boolean
}

// ─── Adapter Input ───

export interface IdeaListAdapterInput {
  showLiquidatedIdeas: boolean
  firm: string
  contributor: string
  category: string
  dateRange: [string | null, string | null]
  onlyMyIdea: boolean
  userSub: string
  userRole: string
  userGroupRole: string
  userFirmRole: string
}

// ─── Request Conversion ───

/**
 * Convert SSRMGridFetchParams + adapter input into the existing IdeaListRequest format.
 * This bridges the gap between ag-grid SSRM params and the backend API contract.
 */
export function toIdeaListRequest(
  params: SSRMGridFetchParams,
  input: IdeaListAdapterInput,
  pageSize: number
): IdeaListRequest {
  const { region, netCategory } = splitCategory(input.category)

  const sortResult = convertSortModel(params.sortModel)
  const orderBy = sortResult ? sortResult[0] : ''
  const orderDirection = sortResult ? sortResult[1] : ''

  const { filterColumn, filterText } = extractFilterFromModel(
    params.filterModel
  )

  // userRole is non-empty only for internal platform roles (Admin, Master, SuperViewer)
  const hasInternalRole = input.userRole !== ''
  const isManagerOrCompliance =
    input.userGroupRole === GROUP_ROLE.ROLE_MANAGER ||
    input.userFirmRole === FIRM_ROLE.ROLE_COMPLIANCE

  const userId =
    input.onlyMyIdea && !hasInternalRole && !isManagerOrCompliance
      ? input.userSub
      : input.contributor || ''

  const rawStartDate = input.dateRange[0]
  const rawEndDate = input.dateRange[1]

  const startDate =
    rawStartDate && dayjs(rawStartDate).isValid()
      ? dayjs(rawStartDate).format('YYYY-MM-DD')
      : undefined

  const endDate =
    rawEndDate && dayjs(rawEndDate).isValid()
      ? dayjs(rawEndDate).format('YYYY-MM-DD')
      : undefined

  return {
    page: Math.floor((params.startRow ?? 0) / pageSize) + 1,
    pageSize,
    includeLiquidated: input.showLiquidatedIdeas,
    firmId: input.firm || '',
    userId,
    region,
    netCategory,
    orderBy,
    orderDirection,
    startDate,
    endDate,
    filterColumn,
    filterText,
  }
}

// ─── Idea Row Transform ───

/**
 * Convert an IdeaListItem into a UnifiedIdeaRow for parent-level display.
 * Fields unavailable from the list API are set to null.
 */
export function toUnifiedIdeaRow(item: IdeaListItem): UnifiedIdeaRow {
  return {
    // Identity & grouping
    id: item.id,
    rowType: 'idea',

    // Shared display fields
    tickerCode: item.tickerCode,
    tickerName: item.tickerName,
    tickerLegalName: item.tickerLegalName ?? null,
    direction: item.direction,
    currentMarketValue: item.currentMarketValue ?? null,
    fillingProgress: item.fillingProgress ?? null,
    dailyReturnPct: item.dailyReturnPct ?? null,
    quarterlyReturnPct: item.quarterlyReturnPct ?? null,
    totalReturnPct: item.totalReturnPct ?? null,
    targetMarketValue: item.targetMarketValue ?? null,
    avgCost: item.avgCost ?? null,
    currentQty: item.shares ?? null,
    targetQty: item.targetQty ?? null,
    tickerCurrency: item.tickerCurrency ?? null,
    targetPrice: item.targetPrice ?? null,
    stopPrice: item.stopPrice ?? null,
    horizon: item.horizon ?? null,
    conviction: item.conviction ?? null,
    tradeReason: item.tradeReason ?? null,
    fillStrategy: item.fillStrategy ?? null,
    auto_fill: item.auto_fill ?? null,
    createdAt: item.createdAt ?? null,
    state: item.state ?? null,
    overallCost: item.overallCost ?? null,
    alerts: item.alert ? [item.alert] : null,
    liquidateReason: item.liquidateReason ?? null,
    closedAt: item.closedAt ?? null,
    duration: item.duration ?? null,
    contributor: item.contributor ?? null,
    firmName: item.firmName ?? null,
    marketTimeZone: item.marketTimeZone ?? null,

    // Idea-only fields
    canEdit: item.canEdit,
    lastFillingState: item.lastFillingState,
    lastFilledResult: item.lastFilledResult,
    isTrial: item.isTrial,
    showAlert: item.showAlert,

    // PnL fields
    dailyPnl: item.dailyPnl ?? null,
    quarterPnl: item.quarterPnl ?? null,
    currentTotalPnl: item.currentTotalPnl ?? null,
    cumulativePnl: item.cumulativePnl ?? null,
    pctGMV: item.pctGMV ?? null,
    pctBP: item.pctBP ?? null,
    shares: item.shares ?? null,
  }
}

// ─── Order Row Transform ───

/**
 * Convert IdeaOrder[] into UnifiedIdeaRow[] for child-level (order) display.
 * Maps order-specific fields onto the shared UnifiedIdeaRow shape.
 * Fields not present on an order are set to null.
 * `marketTimeZone` is inherited from the parent idea, since orders do not
 * carry their own timezone but share the ticker's market timezone.
 */
export function toUnifiedOrderRows(
  orders: IdeaOrder[],
  marketTimeZone: string | null = null
): UnifiedIdeaRow[] {
  return orders.map(
    (order: IdeaOrder): UnifiedIdeaRow => ({
      // Identity & grouping
      id: order.ideaReference,
      rowType: 'order',
      actionId: order.ideaActionId,
      orderId: order.orderId,

      // Shared display fields
      tickerCode: order.tickerCode,
      tickerName: order.tickerName,
      tickerLegalName: order.localName ?? null,
      direction: order.direction,
      currentMarketValue: order.size ?? null,
      fillingProgress: order.fillingProgress ?? null,
      dailyReturnPct: null,
      quarterlyReturnPct: null,
      totalReturnPct: null,
      targetMarketValue: order.targetMarketValue ?? null,
      avgCost: order.avgPrice ?? null,
      currentQty: order.filledQty ?? null,
      targetQty: order.estimateQty ?? null,
      tickerCurrency: order.currency ?? null,
      targetPrice: order.targetPrice ?? null,
      stopPrice: order.stopPrice ?? null,
      horizon: order.horizon ?? null,
      conviction: order.conviction ?? null,
      tradeReason: order.tradeReason ?? null,
      fillStrategy: order.fillStrategy ?? null,
      auto_fill: order.auto_fill ?? null,
      createdAt: order.createdAt ?? null,
      state: order.state ?? null,
      overallCost: order.overallCost ?? null,
      alerts: null,
      liquidateReason: null,
      // Order rows have no closed date / contributor / firm of their own; keep blank.
      closedAt: null,
      duration: null,
      contributor: null,
      firmName: null,
      marketTimeZone: marketTimeZone,

      // Action log specific
      actionType: order.orderType,
      generatedBySystem: order.generatedBy === 'System',
    })
  )
}

// ─── Filter Extraction ───

/**
 * Extract the first valid text filter from the ag-grid filterModel.
 * Maintains the existing single-column filter behavior.
 */
export function extractFilterFromModel(
  filterModel: FilterModel | AdvancedFilterModel | null
): { filterColumn: string; filterText: string } {
  if (!filterModel || typeof filterModel !== 'object') {
    return { filterColumn: '', filterText: '' }
  }

  // AdvancedFilterModel has a 'filterType' at root level — skip it
  if ('filterType' in filterModel) {
    return { filterColumn: '', filterText: '' }
  }

  const entries = Object.entries(filterModel as FilterModel)
  if (entries.length === 0) {
    return { filterColumn: '', filterText: '' }
  }

  // Take the first valid text filter entry
  for (const [colId, model] of entries) {
    if (
      model &&
      typeof model === 'object' &&
      'filter' in model &&
      model.filter
    ) {
      const backendField = sortMappingIdeaList[colId] || colId
      return { filterColumn: backendField, filterText: String(model.filter) }
    }
  }

  return { filterColumn: '', filterText: '' }
}

// ─── Internal Helpers ───

function convertSortModel(sortModel: SortModelItem[]): [string, string] | null {
  if (sortModel.length > 0) {
    const orderBy = sortModel[0].colId
    const order = sortModel[0].sort.toUpperCase()
    return [orderBy, order]
  }
  return null
}
